import {useCheckoutTrackingUrl, useShopId} from '@aboutyou/config';
// eslint-disable-next-line monorepo/no-internal-import
import {useAppUrlParams} from '@aboutyou/logic-components/src/backend-services/Checkout/hooks/useAppUrlParams';
// eslint-disable-next-line monorepo/no-internal-import
import {DEVICE_PARAM_TYPE} from '@aboutyou/logic-components/src/features/Checkout/constants';
import {Log} from '@aboutyou/mlm';
import {useCustomerBiData, useCustomerId, useUser} from '@aboutyou/user';
import {useRef} from 'react';

export type Device = 'mobile-web' | 'desktop-web' | 'ios-app' | 'android-app';

export type ExpressPaymentProviders = 'klarna' | 'paypal';

interface ComponentLoadEvent {
  type: 'ComponentLoadEvent';
  expressFallback?: boolean;
}

interface IntentConfirmationEvent {
  type: 'IntentConfirmationEvent';
  orderId: number;
}

interface OSPEvent {
  type: 'OSPEvent';
  cbd: string;
  paymentType?: string;
  orderDate?: string;
}

interface CheckoutErrorEvent {
  type: 'CheckoutError';
  message?: string;
  endpoint?: string;
  statusCode?: string;
}

interface ExpressCheckoutReviewError {
  type: 'ExpressCheckoutReviewError';
  message?: string;
  endpoint?: string;
  statusCode?: string;
}

interface CheckoutLoadTiming {
  type: 'CheckoutLoadTiming';
  duration: number;
  startTrigger:
    | 'checkout-page-entry'
    | 'web-component-render-start'
    | 'express-checkout-page-entry'
    | 'express-web-component-render-start'
    | 'go-to-checkout-click'
    | 'before-app-go-to-checkout-click'
    | 'express-payment-method-authorization'
    | 'app-express-payment-method-authorization';
  endTrigger: 'checkout-load' | 'express-checkout-review-load';
}

interface ComponentPreloadErrorEvent {
  type: 'ComponentPreloadError';
  message: string;
}

interface ExpressCheckoutEntryEvent {
  type: 'ExpressCheckoutEntry';
  pc180Score?: number;
  expressPaymentKey: ExpressPaymentProviders;
}

interface ExpressCheckoutCancelEvent {
  type: 'ExpressCheckoutCancel';
  pc180Score?: number;
  expressPaymentKey: ExpressPaymentProviders;
}

interface ExpressCheckoutAuthorizationEvent {
  type: 'ExpressCheckoutAuthorization';
  pc180Score?: number;
  expressPaymentKey: ExpressPaymentProviders;
  review: boolean;
}

interface ExpressCheckoutWidgetError {
  type: 'ExpressCheckoutWidgetError';
  pc180Score?: number;
  expressPaymentKey: ExpressPaymentProviders;
  errorReason: string;
  statusCode: number;
  errorKey: string;
}

interface ExpressCheckoutReviewLoadEvent {
  type: 'ExpressCheckoutReviewLoad';
  pc180Score?: number;
  expressPaymentKey: ExpressPaymentProviders | 'N/A';
}

interface ExpressOrderSuccessPageEvent {
  type: 'ExpressOrderSuccessPage';
  customerId: number | null;
  pc180Score?: number;
  expressPaymentKey: ExpressPaymentProviders;
  orderDate: string;
  orderId: string;
  paymentType: string;
}

interface AccessTokenReloadSuccessEvent {
  type: 'AccessTokenReloadSuccessEvent';
  tokenId: string;
  authProvider: string | null;
}

interface AccessTokenReloadErrorEvent {
  type: 'AccessTokenReloadErrorEvent';
  errorDetail: string;
  code?: number;
  authProvider: string | null;
}

interface CheckoutUnresponsiveErrorEvent {
  type: 'CheckoutUnresponsiveErrorEvent';
}

type Event =
  | ComponentLoadEvent
  | IntentConfirmationEvent
  | OSPEvent
  | CheckoutErrorEvent
  | CheckoutLoadTiming
  | ComponentPreloadErrorEvent
  | ExpressCheckoutEntryEvent
  | ExpressCheckoutCancelEvent
  | ExpressCheckoutAuthorizationEvent
  | ExpressCheckoutReviewLoadEvent
  | ExpressOrderSuccessPageEvent
  | AccessTokenReloadSuccessEvent
  | AccessTokenReloadErrorEvent
  | ExpressCheckoutWidgetError
  | ExpressCheckoutReviewError
  | CheckoutUnresponsiveErrorEvent;

export const useCOTracking = () => {
  const checkoutTrackingUrl = useCheckoutTrackingUrl();
  const biData = useCustomerBiData();
  const {data: user} = useUser();

  const shopId = useShopId();
  const {device: appDeviceParam, customerId: appCustomerIdParam} =
    useAppUrlParams();
  const trackingDevice = useRef(getDevice(appDeviceParam)).current;

  const webCustomerId = useCustomerId();
  const customerId = appCustomerIdParam || webCustomerId;

  async function track(event: Event) {
    const trackingPath = getCoTrackingPath(event);
    const endpoint = `${checkoutTrackingUrl}${trackingPath}`;

    try {
      const {type, ...body} = event;
      const res = await fetch(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          ...body,
          shopId,
          device: trackingDevice,
          customerId: 'customerId' in body ? body.customerId : customerId,
          // on app we dont get biData and default value is 0
          // we want mark it a bit different so that we know differentiate between default and actual value
          pc180Score: user ? biData.pc180Score : -1,
        }),
        priority: 'low',
        keepalive: true,
      } as RequestInit);
      if (process.env.NODE_ENV !== 'production') {
        Log.info(
          '@aboutyou/tracking',
          `COT event fired successfully for ${type}`,
          {
            event,
            res,
          },
        );
      }
    } catch (error) {
      Log.error(
        '@aboutyou/tracking',
        'Something went wrong calling COT endpoint',
        {error},
      );
    }
  }

  return {
    track,
    customerId,
    trackingDevice,
    checkoutTrackingUrl,
  };
};

function getCoTrackingPath(event: Event) {
  switch (event.type) {
    case 'ComponentPreloadError':
      return '/preload-checkout-error';
    case 'ComponentLoadEvent':
      return '/checkout-load';
    case 'IntentConfirmationEvent':
      return '/intent-confirmation';
    case 'CheckoutError':
      return '/checkout-error';
    case 'OSPEvent':
      return '/order-success-page';
    case 'CheckoutLoadTiming':
      return '/load-timing';
    case 'ExpressCheckoutEntry':
      return '/express-checkout-entry';
    case 'ExpressCheckoutCancel':
      return '/express-checkout-cancel';
    case 'ExpressCheckoutWidgetError':
      return '/express-checkout-widget-error';
    case 'ExpressCheckoutReviewError':
      return '/express-checkout-review-error';
    case 'ExpressCheckoutReviewLoad':
      return '/express-checkout-review-load';
    case 'ExpressOrderSuccessPage':
      return '/express-order-success-page';
    case 'ExpressCheckoutAuthorization':
      return '/express-checkout-authorization';
    case 'AccessTokenReloadSuccessEvent':
      return '/access-token-reload-success';
    case 'AccessTokenReloadErrorEvent':
      return '/access-token-reload-error';
    case 'CheckoutUnresponsiveErrorEvent':
      return '/unresponsive-checkout-error';
    default:
      throw new Error('[CO tracking] unknown event type');
  }
}

function getDevice(appDevice: DEVICE_PARAM_TYPE): Device {
  if (appDevice === 'ios') {
    return 'ios-app';
  }
  if (appDevice === 'android') {
    return 'android-app';
  }
  if (process.env.STACK === 'mobile') {
    return 'mobile-web';
  }
  if (process.env.STACK === 'desktop') {
    return 'desktop-web';
  }

  return 'invalid-device' as Device;
}
